import { useCallback, useEffect, useState } from "react"
import PropTypes from "prop-types"
import { Button, Flex, Result, Spin } from "antd"
import { useTranslation } from "react-i18next"
import { Text } from "ui/Typography"
import PowerTranzCheckout from "ui/PowerTranzCheckout"
import DiscardConfirm from "ui/DiscardModal"
import { spacings } from "themes/variables"
import { numberToCurrency } from "helpers/string"
import { clearAttemptRef, getPowerTranzErrorMessage, getSavedAttemptRef, isConfirmedAttemptState } from "helpers/powertranz"
import { usePowerTranzStatusPolling } from "hooks/usePowerTranzStatusPolling"
import { ATTEMPT_STATES, CONFIRMING_STATES } from "constants/powertranz"

// Charges the card for a booking the guide just saved, on PowerTranz's hosted page in its own
// window (the checkout's isStayingOnPage). The outcome always comes from the status endpoint,
// polled from the moment the hosted page opens; the popup's own result page is never relied on.
const PowerTranzCardPayment = ({ bookingId, onViewBooking, onClose }) => {
  const { t } = useTranslation()
  const [attemptRef, setAttemptRef] = useState(null)
  const [isSessionLive, setIsSessionLive] = useState(false)
  // The card was submitted (result message, or the checkout found the attempt already settling).
  const [isAwaitingOutcome, setIsAwaitingOutcome] = useState(false)
  // Remounts the checkout for a new attempt after a failure or expiry.
  const [checkoutKey, setCheckoutKey] = useState(0)
  const [isCloseConfirmOpen, setIsCloseConfirmOpen] = useState(false)

  const { status: polledStatus, isRequestFailed, isTimedOut } = usePowerTranzStatusPolling(attemptRef ? { attempt_ref: attemptRef } : null)
  // After a retry the last answer is about the previous attempt.
  const status = polledStatus?.attemptRef === attemptRef ? polledStatus : null
  const state = status?.state
  const isConfirmed = isConfirmedAttemptState(state)
  const isFailed = state === ATTEMPT_STATES.FAILED
  const isExpired = isAwaitingOutcome && state === ATTEMPT_STATES.EXPIRED
  const isConfirming = !isConfirmed && !isFailed && !isExpired && (CONFIRMING_STATES.includes(state) || isAwaitingOutcome)

  useEffect(() => {
    if (isConfirmed || isFailed) clearAttemptRef(bookingId)
  }, [isConfirmed, isFailed, bookingId])

  const handleSessionChange = useCallback(
    (isLive) => {
      setIsSessionLive(isLive)
      if (isLive) setAttemptRef(getSavedAttemptRef(bookingId))
    },
    [bookingId]
  )

  const handleResult = useCallback((resultAttemptRef) => {
    if (resultAttemptRef) setAttemptRef(resultAttemptRef)
    setIsAwaitingOutcome(true)
  }, [])

  const handleRetry = () => {
    clearAttemptRef(bookingId)
    setAttemptRef(null)
    setIsAwaitingOutcome(false)
    setCheckoutKey((key) => key + 1)
  }

  // Leaving while the hosted page is open is safe (the attempt expires, nothing is charged) but the
  // guide should mean it.
  const handleCloseRequest = () => (isSessionLive ? setIsCloseConfirmOpen(true) : onClose())

  const viewBookingButton = (
    <Button type="primary" onClick={onViewBooking}>
      {t("powertranz.booking_form.view_booking")}
    </Button>
  )

  if (isConfirmed) {
    return (
      <Result
        status="success"
        title={t("powertranz.booking_form.paid_title")}
        subTitle={status.income?.amount != null && t("powertranz.result.amount_paid", { amount: numberToCurrency(status.income.amount) })}
        extra={viewBookingButton}
      />
    )
  }

  if (isFailed || isExpired) {
    return (
      <Result
        status="error"
        title={t("powertranz.booking_form.failed_title")}
        subTitle={isExpired ? t("powertranz.errors.expired") : getPowerTranzErrorMessage(t, status.failureReason)}
        extra={[
          <Button key="retry" type="primary" onClick={handleRetry}>
            {isExpired ? t("powertranz.checkout.start_again") : t("powertranz.checkout.try_again")}
          </Button>,
          <Button key="close" onClick={onClose}>
            {t("powertranz.booking_form.close")}
          </Button>
        ]}
      />
    )
  }

  // The charge may already have gone through: never offer to pay again from here.
  if (isConfirming || isTimedOut) {
    return (
      <Result
        icon={isTimedOut ? undefined : <Spin size="large" />}
        status={isTimedOut ? "info" : undefined}
        title={isTimedOut ? t("powertranz.booking_form.still_confirming_title") : t("powertranz.result.confirming_title")}
        subTitle={
          (isTimedOut && t("powertranz.booking_form.still_confirming_text")) ||
          (isRequestFailed && t("powertranz.result.status_unreachable")) ||
          t("powertranz.result.confirming_text")
        }
        extra={viewBookingButton}
      />
    )
  }

  return (
    <Flex vertical gap={spacings.M}>
      <Text strong>{t("powertranz.booking_form.pay_title")}</Text>
      <Text>{t("powertranz.booking_form.pay_intro")}</Text>
      <PowerTranzCheckout
        key={checkoutKey}
        bookingId={bookingId}
        onSessionChange={handleSessionChange}
        onResult={handleResult}
        isStayingOnPage
      />
      <Flex justify="flex-end">
        <Button onClick={handleCloseRequest}>{t("powertranz.booking_form.close")}</Button>
      </Flex>
      <DiscardConfirm
        open={isCloseConfirmOpen}
        onCancel={() => setIsCloseConfirmOpen(false)}
        onConfirm={onClose}
        title={t("powertranz.booking_form.close_confirm_title")}
        description={t("powertranz.booking_form.close_confirm_text")}
        t={t}
      />
    </Flex>
  )
}

PowerTranzCardPayment.propTypes = {
  bookingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onViewBooking: PropTypes.func.isRequired,
  // Leaves the booking saved with the amount unpaid.
  onClose: PropTypes.func.isRequired
}

export default PowerTranzCardPayment
